import { z } from 'zod';
import z4 from 'zod/v4/core';
import { DateTimeSchema } from './dates.js';
import { ObjectId } from './objectId.js';
import { isObjectType, isString } from 'remeda';
import { AccessLevel } from '../permissions.js';

export * from './dates.js';

//--------------------------------------------------------------------------------------------------------------------//
//region Misc schemas
export function isObjectId(val: unknown): val is ObjectId {
  return (
    val instanceof ObjectId ||
    (isObjectType(val) && '_bsontype' in val && val._bsontype === 'ObjectId')
  );
}

const objectIdInstance = z.custom<ObjectId>(isObjectId, { params: { name: 'ObjectId' } });
const objectIdString = z.string().regex(/^[\da-f]{24}$/i, 'Invalid ObjectId');
const objectIdOrString = objectIdInstance.or(objectIdString);
const objectIdOrStringFromObject = z.object({ _id: objectIdOrString }).transform(({ _id }) => _id);

const coerceObjectId = objectIdOrString
  .transform((val) => (isString(val) ? new ObjectId(val) : val))
  .pipe(objectIdInstance);
const coerceObjectIdString = objectIdOrString
  .transform((val) => val.toString())
  .pipe(objectIdString);

export const ObjectIdString = objectIdOrString
  .or(objectIdOrStringFromObject)
  .pipe(coerceObjectIdString);
export type ObjectIdString = z.infer<typeof ObjectIdString>;

// Simpler format without the dynamic inputs for frontend stuff, maybe
export const SimpleObjectIdString = objectIdString;
export type SimpleObjectIdString = z.infer<typeof SimpleObjectIdString>;

// Schema that allows ObjectIds or strings
export const ObjectIdSchema = objectIdOrString.or(objectIdOrStringFromObject).pipe(coerceObjectId);

// Registry to track which model each ObjectIdRef type points at
type MongooseRegistryMetadata = {
  // Properties to override directly on the Mongoose schema
  schemaProps?: {
    ref?: string;
  };
  typeOverride?: 'Mixed' | 'ObjectId' | 'SubDocument';
};
export const mongooseZodRegistry = z.registry<MongooseRegistryMetadata>();
export const objectIdRef = (ref: string) => {
  const schema = ObjectIdSchema.or(z.object({ _id: ObjectIdSchema }));
  mongooseZodRegistry.add(schema, { schemaProps: { ref }, typeOverride: 'ObjectId' });
  return schema;
};

// Explicitly marks an object as a subDocument as opposed to nested paths
export const subDocument = <Z extends z4.$ZodObject>(schema: Z): Z => {
  mongooseZodRegistry.add(schema, { typeOverride: 'SubDocument' });
  return schema;
};

// Marks a schema as 'Mixed' for any models that use it
export const mixedType = <Z extends z4.$ZodType>(schema: Z): Z => {
  mongooseZodRegistry.add(schema, { typeOverride: 'Mixed' });
  return schema;
};

export type UnknownObject = Record<string, unknown>;
export const UnknownObject = z.record(z.string(), z.unknown());

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Opportunity API schemas

// Unimplemented opportunity types that only exist on our demo page
export const demoOpportunityTypes = [
  'placementGhostingRisk',
  'submissionBackOutRisk',
  'submissionGhostingRisk',
  'callOffEscalation',
  'facilityCanceledShift',
  'redeploymentRisk',
  'redeploymentRiskNotEngaged',
  'outreachResponseTrendUp',
  'outreachResponseTrendDown',
  'engagementAttemptTrendUp',
  'engagementAttemptTrendDown',

  'clientRequestReminder',
  'clientVisitNoteQualityTrendUp',
  'clientVisitNoteQualityTrendDown',
  'clientVisitWithMinimalData',
  'competitorThemeTrendUp',
] as const;
export const DemoOpportunityType = z.enum(demoOpportunityTypes);
export type DemoOpportunityType = z.infer<typeof DemoOpportunityType>;

// Actual opportunity types
export const realOpportunityTypes = [
  'followUpFailure',
  'placementBackOutRisk',
  'placementReportingFrustration',
  'leadAbandonment',
  'lossToCompetitor',
  'responseTimeImprovement',
  'responseTimeDecline',
  'phoneScreenFollowUp',
  'handoffFailure',
  'inboundFollowUp',
  'workersCompLegalRisk',
] as const;
export const OpportunityType = z.enum(realOpportunityTypes);
export type OpportunityType = z.infer<typeof OpportunityType>;

export const realAndDemoOpportunityTypes = [
  ...realOpportunityTypes,
  ...demoOpportunityTypes,
] as const;
export const RealOrDemoOpportunityType = z.enum(realAndDemoOpportunityTypes);
export type RealOrDemoOpportunityType = z.infer<typeof RealOrDemoOpportunityType>;

export const OpportunityStatus = z.enum([
  'unverified',
  'verifying',
  'error',
  'open',
  'expired',
  'complete',
  'invalid',
]);
export type OpportunityStatus = z.infer<typeof OpportunityStatus>;

export const internalDomain = 'toroinsights';

export const TagType = z.enum([
  'Candidate',
  'Recruiter',
  'Placement',
  'Position',
  'Submission',
  'Company',
  'Team',
  'Manager',
]);
export type TagType = z.infer<typeof TagType>;

export const OpportunityTagDTO = z.object({
  tagType: TagType,
  entity: ObjectIdString.optional(),
  entitySourceId: z.string().optional(),
  name: z.string().optional(),
});
export type OpportunityTagDTO = z.infer<typeof OpportunityTagDTO>;

export const OpportunityTagFilterValue = z.object({
  tagType: TagType.optional(),
  entity: ObjectIdString,
  entitySourceId: z.string().optional(),
  name: z.string().optional(),
});
export type OpportunityTagFilterValue = z.infer<typeof OpportunityTagFilterValue>;

export const OpportunityTagFilterOption = z.object({
  ...OpportunityTagFilterValue.shape,
  count: z.number().optional(),
});
export type OpportunityTagFilterOption = z.infer<typeof OpportunityTagFilterOption>;

export const OpportunityTagFilters = z.partialRecord(TagType, OpportunityTagFilterValue.array());
export type OpportunityTagFilters = z.infer<typeof OpportunityTagFilters>;

export const OpportunityOtherFilters = z.object({
  opportunityTypes: RealOrDemoOpportunityType.array().default([]),
  accounts: ObjectIdString.array().default([]),
  departments: z.string().array().default([]),
});
export type OpportunityOtherFilters = z.infer<typeof OpportunityOtherFilters>;

export const OpportunitySortOption = z.enum([
  'score',
  '-score',
  'date',
  '-date',
  'completedDate',
  '-completedDate',
]);
export type OpportunitySortOption = z.infer<typeof OpportunitySortOption>;

export const OpportunityDateFieldTarget = z.enum(['created', 'completed']);
export type OpportunityDateFieldTarget = z.infer<typeof OpportunityDateFieldTarget>;

export const OpportunityDateFieldPath = z.enum(['createdAt', 'data.completedDate']);
export type OpportunityDateFieldPath = z.infer<typeof OpportunityDateFieldPath>;

export const DateFilterPresetOption = z.enum([
  'today',
  'yesterday',
  'last7days',
  'last30days',
  'last90days',
]);
export type DateFilterPresetOption = z.infer<typeof DateFilterPresetOption>;

export const DateFilterType = z.enum(['exact', 'range', 'before', 'after', 'preset']);
export type DateFilterType = z.infer<typeof DateFilterType>;

const BaseDateFilter = z.object({
  type: DateFilterType,
  fieldName: z.string().optional(),
  fieldLabel: z.string().optional(),
});
export const PresetDateFilter = z.object({
  ...BaseDateFilter.shape,
  type: z.literal('preset'),
  preset: DateFilterPresetOption,
});
export type PresetDateFilter = z.infer<typeof PresetDateFilter>;

// TODO: make date fields required, but fix frontend pickers to support partials
export const RangeDateFilter = z.object({
  ...BaseDateFilter.shape,
  type: z.literal('range'),
  startDate: DateTimeSchema.nullish(),
  endDate: DateTimeSchema.nullish(),
});
export type RangeDateFilter = z.infer<typeof RangeDateFilter>;
export const SingleDateFilter = z.object({
  ...BaseDateFilter.shape,
  type: DateFilterType.extract(['exact', 'before', 'after']),
  date: DateTimeSchema.nullish(),
});
export type SingleDateFilter = z.infer<typeof SingleDateFilter>;

export const CustomDateFilter = z.discriminatedUnion('type', [RangeDateFilter, SingleDateFilter]);
export type CustomDateFilter = z.infer<typeof CustomDateFilter>;

export const DateFilter = z.discriminatedUnion('type', [CustomDateFilter, PresetDateFilter]);
export type DateFilter = z.infer<typeof DateFilter>;

// DateFilter with an extra `target` field added
// TODO: replace with fieldName as an enum
export const OpportunityDateFilter = z.intersection(
  DateFilter,
  z.object({
    target: OpportunityDateFieldTarget.nullish(),
  })
);
export type OpportunityDateFilter = z.infer<typeof OpportunityDateFilter>;

export const BackendOpportunityFilters = z.object({
  tagFilters: z
    .object({
      tagType: TagType,
      values: ObjectIdString.array(),
    })
    .array(),
  otherFilters: OpportunityOtherFilters,
  dateFilter: z
    .object({
      startDate: DateTimeSchema.nullable(),
      endDate: DateTimeSchema.nullable(),
      target: OpportunityDateFieldPath,
    })
    .nullish(),
});
export type BackendOpportunityFilters = z.infer<typeof BackendOpportunityFilters>;

export const OpportunityFilters = z.object({
  tagFilters: OpportunityTagFilters,
  otherFilters: OpportunityOtherFilters,
  dateFilter: OpportunityDateFilter.nullable(),
});
export type OpportunityFilters = z.infer<typeof OpportunityFilters>;

export const SavableOpportunityFilters = OpportunityFilters.omit({
  dateFilter: true,
});
export type SavableOpportunityFilters = z.infer<typeof SavableOpportunityFilters>;

// TODO: remove this once getFilterOptions migration is complete
export const OpportunityFilterOptionsQuery = z.object({
  status: OpportunityStatus.array().default(['open']),
  completeSubStatus: z.enum(['manual', 'automatic']).array().optional(),
  readStatus: z.enum(['read', 'unread']).array().optional(),
  filters: BackendOpportunityFilters.optional(),
  demo: z.boolean().optional(),
});
export type OpportunityFilterOptionsQuery = z.infer<typeof OpportunityFilterOptionsQuery>;

export const OpportunityFilterOptionsInput = OpportunityFilterOptionsQuery.safeExtend({
  tagType: TagType,
});
export type OpportunityFilterOptionsInput = z.infer<typeof OpportunityFilterOptionsInput>;

export const EnrichedTagFilters = z
  .object({
    tagType: TagType,
    values: z
      .object({
        entity: ObjectIdString,
        name: z.string(),
      })
      .array(),
  })
  .array();

export type EnrichedTagFilters = z.infer<typeof EnrichedTagFilters>;

export const OpportunityListQuery = z.object({
  ...OpportunityFilterOptionsQuery.shape,
  page: z.number().min(1).default(1),
  count: z.number().min(1).max(100).default(50),
  sort: OpportunitySortOption.default('score'),
  debugInfo: z.boolean().optional(),

  analyticsType: z.string().nullish(),
  opportunityIds: ObjectIdString.array().nullish(),

  // Optional role override for visibility filtering (only honored for toroAdmin users)
  simulatedRole: AccessLevel.optional(),
});
export type OpportunityListQuery = z.infer<typeof OpportunityListQuery>;

export const OperationExportFormats = z.enum(['csv', 'xlsx', 'pdf']);
export type OperationExportFormat = z.infer<typeof OperationExportFormats>;

export const OpportunityExportFormat = z.enum(['csv', 'xlsx', 'pdf']);
export type OpportunityExportFormat = z.infer<typeof OpportunityExportFormat>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

// Bullhorn entity types we sync and build activity data for. Insights and chats anchor to these
export const ActivityDataEntityType = z.enum(['Candidate', 'Contact', 'Company']);
export type ActivityDataEntityType = z.infer<typeof ActivityDataEntityType>;

// Entities an LlmResponse can reference; wider than ActivityDataEntityType (jobs have no activity timeline)
export const LlmResponseEntityType = z.enum([...ActivityDataEntityType.options, 'Position']);
export type LlmResponseEntityType = z.infer<typeof LlmResponseEntityType>;

export const InputEntity = z.object({
  type: ActivityDataEntityType,
  id: z.string().describe('entity _id or sourceId'),
  name: z.string().nullish(),
});
export type InputEntity = z.infer<typeof InputEntity>;

// Bullhorn entity types the extension tracks while the user browses.
export const TrackedEntityType = z.enum([
  ...ActivityDataEntityType.options,
  'JobOrder',
  'Placement',
  'Lead',
  'Task',
  'Opportunity',
  'Tearsheet',
]);
export type TrackedEntityType = z.infer<typeof TrackedEntityType>;

export const TrackedEntity = z.object({
  ...InputEntity.shape,
  type: TrackedEntityType,
});
export type TrackedEntity = z.infer<typeof TrackedEntity>;

export const FieldFilterStatus = z.enum(['Set', 'Not Set']);
export type FieldFilterStatus = z.infer<typeof FieldFilterStatus>;
export const FieldFilterOperation = z.enum(['AND', 'OR', 'EXACT', 'NONE']);
export type FieldFilterOperation = z.infer<typeof FieldFilterOperation>;

type OperatorDisplay = { joiner: string; prefix: string };

const FIELD_FILTER_OPERATION_DISPLAY = {
  AND: { joiner: ' AND ', prefix: 'ALL ' },
  NONE: { joiner: ' NOR ', prefix: 'NOT ' },
  EXACT: { joiner: ', ', prefix: 'ONLY ' },
  OR: { joiner: ', ', prefix: 'ANY ' },
} as const satisfies Record<FieldFilterOperation, OperatorDisplay>;

export function getOperatorDisplayProps(operator?: FieldFilterOperation | null): OperatorDisplay {
  return FIELD_FILTER_OPERATION_DISPLAY[operator ?? 'OR'];
}

export const FieldFilter = z.object({
  name: z.string(),
  status: FieldFilterStatus.nullish(),
  values: z
    .union([
      // Keep backward compatibility
      z.number(),
      z.string(),
      z.object({
        value: z.number().or(z.string()),
        label: z.string(),
      }),
    ])
    .array()
    .nullish(),
  operator: FieldFilterOperation.nullish(),
  excludedValues: z.union([z.number(), z.string()]).array().nullish(),
  label: z.string().nullish(),
});
export type FieldFilter = z.infer<typeof FieldFilter>;

const FieldQueryMeta = z.object({
  path: z.string(),
  type: z.enum(['string', 'reference', 'number', 'boolean']),
  zeroMeansUnset: z.boolean().optional(),
});
export type FieldQueryMeta = z.infer<typeof FieldQueryMeta>;

export const SearchEntityQuery = z.object({
  fieldFilters: z.array(FieldFilter),
  // TODO: dedupe these 2
  // dateFilter: DateFilter.nullish(),
  dateFilters: DateFilter.array().optional(),
  fields: z.string().nullish(),
  count: z.number().min(1).default(100),
  sampleResults: z.boolean().default(true),
  totalPopulation: z.number().optional(),
  confidenceLevel: z.number().optional(),
  marginOfError: z.number().optional(),
});
export type SearchEntityQuery = z.infer<typeof SearchEntityQuery>;

const metadataSchema = z.object({
  startDate: DateTimeSchema,
  endDate: DateTimeSchema,
  periodLength: z.number(),
});

// TODO: use schema
// export const StrategicThemeSchema = z.object({
//     id: z.string(),
//     title: z.string(),
//     description: z.string(),
//     priority: z.enum(['High', 'Medium', 'Low']),
//     color: z.string(),
//     metrics: z.array(
//         z.object({
//             name: z.string(),
//             value: z.string(),
//             status: z.enum(['critical', 'warning', 'success']),
//         })
//     ),
//     contributingFactors: z.array(
//         z.object({
//             name: z.string(),
//             value: z.number(),
//             link: z.enum(['lossToCompetitor', 'followUpFailure', 'placementBackOutRisk']),
//         })
//     ),
//     recommendation: z.string(),
//     actionItems: z.array(z.string()),
//     impact: z.string(),
// });
export const StrategicThemeSchema = z.unknown();

export type StrategicTheme = z.infer<typeof StrategicThemeSchema>;
